import { useState } from 'react';

const FORM_ID = 'db69j2vqhq9pk5mecrng';
const SUBMIT_ADDR = 'https://readdy.ai/api/form/db69j2vqhq9pk5mecrng';

export default function ContactForm() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = ((formData.get('website_alt') as string) || '').trim();
    if (honeypot) {
      setStatus('success');
      form.reset();
      return;
    }
    formData.delete('website_alt');

    setStatus('submitting');
    setFormError('');

    try {
      const body = new URLSearchParams(formData as unknown as Record<string, string>).toString();
      const response = await fetch(SUBMIT_ADDR, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');

      if (response.ok && parsed?.code === 'OK' && !isSpam) {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
        setFormError(serverMsg || '提交失败，请稍后重试。');
      }
    } catch {
      setStatus('error');
      setFormError('网络异常，提交失败，请稍后重试。');
    }
  };

  if (status === 'success') {
    return (
      <div className="flex flex-col items-center rounded-lg border border-background-200 bg-background-50 py-14 text-center">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-accent-100 text-accent-700">
          <i className="ri-checkbox-circle-line text-3xl" />
        </span>
        <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">留言提交成功</h3>
        <p className="mt-2 max-w-sm text-sm leading-6 text-foreground-500">
          感谢你的来信，爆料网编辑部会尽快阅读并回复。若涉及新闻线索，我们会视情况跟进核实。
        </p>
        <button
          type="button"
          onClick={() => setStatus('idle')}
          className="mt-5 inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-background-300 bg-background-100 px-4 py-2 text-sm font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
        >
          <i className="ri-edit-line" />
          再写一条
        </button>
      </div>
    );
  }

  return (
    <form
      id={FORM_ID}
      data-readdy-form
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7"
    >
      <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-foreground-950">
        <i className="ri-chat-3-line text-primary-500" />
        在线留言
      </h2>
      <p className="mt-1 text-sm text-foreground-500">填写下方信息，我们会尽快与你取得联系。</p>

      <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <label htmlFor="contact-name" className="text-sm font-medium text-foreground-700">
            姓名 <span className="text-secondary-500">*</span>
          </label>
          <input
            id="contact-name"
            name="name"
            type="text"
            required
            placeholder="请输入你的姓名"
            className="h-11 rounded-md border border-background-300 bg-background-100 px-3 text-sm text-foreground-800 outline-none transition-colors focus:border-primary-400 focus:bg-background-50"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label htmlFor="contact-email" className="text-sm font-medium text-foreground-700">
            邮箱 <span className="text-secondary-500">*</span>
          </label>
          <input
            id="contact-email"
            name="email"
            type="email"
            required
            placeholder="请输入常用邮箱"
            className="h-11 rounded-md border border-background-300 bg-background-100 px-3 text-sm text-foreground-800 outline-none transition-colors focus:border-primary-400 focus:bg-background-50"
          />
        </div>
      </div>

      <div className="mt-4 flex flex-col gap-1.5">
        <label htmlFor="contact-subject" className="text-sm font-medium text-foreground-700">
          留言主题
        </label>
        <input
          id="contact-subject"
          name="subject"
          type="text"
          placeholder="如：新闻线索 / 内容建议 / 商务合作"
          className="h-11 rounded-md border border-background-300 bg-background-100 px-3 text-sm text-foreground-800 outline-none transition-colors focus:border-primary-400 focus:bg-background-50"
        />
      </div>

      <div className="mt-4 flex flex-col gap-1.5">
        <label htmlFor="contact-message" className="text-sm font-medium text-foreground-700">
          留言内容 <span className="text-secondary-500">*</span>
        </label>
        <textarea
          id="contact-message"
          name="message"
          required
          maxLength={500}
          rows={5}
          placeholder="请描述你的问题或线索（不超过500字）"
          className="rounded-md border border-background-300 bg-background-100 px-3 py-2.5 text-sm leading-6 text-foreground-800 outline-none transition-colors focus:border-primary-400 focus:bg-background-50"
        />
      </div>

      <input
        type="text"
        name="website_alt"
        className="form-guard-field"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      {status === 'error' && formError ? (
        <p className="mt-4 rounded-md border border-secondary-200 bg-secondary-50 px-3 py-2 text-sm text-secondary-700">
          {formError}
        </p>
      ) : null}

      <button
        type="submit"
        disabled={status === 'submitting'}
        className="mt-5 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-600 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700 disabled:cursor-not-allowed disabled:opacity-70"
      >
        <i className={status === 'submitting' ? 'ri-loader-4-line animate-spin' : 'ri-send-plane-line'} />
        {status === 'submitting' ? '提交中...' : '提交留言'}
      </button>
    </form>
  );
}